import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLoginMutation } from '@/api/queries/auth';
import { PATH } from '@/router/PATH';

export type UseLoginResult = {
    email: string;
    password: string;
    hasError: boolean;
    isSubmitting: boolean;
    isValid: boolean;
    onEmailChange: (value: string) => void;
    onPasswordChange: (value: string) => void;
    onSubmit: () => void;
};

/**
 * Login form state + submit. Every failure (unknown email, wrong password,
 * disabled account, transport error) collapses to a single `hasError` flag —
 * the view renders one generic message, never leaking which part was wrong. On
 * success the mutation has already persisted the session, so we just navigate
 * home.
 */
export const useLogin = (): UseLoginResult => {
    const navigate = useNavigate();
    const loginMutation = useLoginMutation();

    const [email, setEmail] = React.useState('');
    const [password, setPassword] = React.useState('');
    const [hasError, setHasError] = React.useState(false);

    const onEmailChange = React.useCallback((value: string) => {
        setHasError(false);
        setEmail(value);
    }, []);

    const onPasswordChange = React.useCallback((value: string) => {
        setHasError(false);
        setPassword(value);
    }, []);

    const trimmedEmail = email.trim();
    const isValid = trimmedEmail.length > 0 && password.length > 0;

    const onSubmit = React.useCallback(() => {
        if (!isValid || loginMutation.isPending) {
            return;
        }
        setHasError(false);
        loginMutation.mutate(
            { email: trimmedEmail, password },
            {
                onSuccess: (result) => {
                    if (result.success) {
                        navigate(PATH.HOME, { replace: true });
                    } else {
                        setHasError(true);
                    }
                },
                onError: () => {
                    setHasError(true);
                },
            },
        );
    }, [isValid, loginMutation, navigate, password, trimmedEmail]);

    return {
        email,
        password,
        hasError,
        isSubmitting: loginMutation.isPending,
        isValid,
        onEmailChange,
        onPasswordChange,
        onSubmit,
    };
};
